import { Alert, Box, Chip, FormControl, InputLabel, MenuItem, Paper, Select, Stack, TextField, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { FaEye, FaRegCreditCard } from 'react-icons/fa';
import { IoFilterOutline } from "react-icons/io5";
import { MdAttachMoney } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { Can } from '../../../Components/Can';
import { ConfirmDialog } from '../../../Components/Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import { OrderFiltersDrawer } from '../../../Components/Drawer/variants/orders-filters/orders-filters-drawer.component';
import { Button } from '../../../Components/Form/Button/button.scomponent';
import { ButtonActionMenu } from '../../../Components/Form/ButtonActionsMenu/button-action-menu.component';
import { TableListSkeleton } from '../../../Components/Table/table-list-skeleton.component';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { Table } from '../../../Components/Table/table.component';
import { Tag } from '../../../Components/Tag/tag.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { BillProductOrderOrdersUseCase } from '../../../Usecase/Orders/bill-products-order-usecase';
import { ListOrdersUseCase } from '../../../Usecase/Orders/list-orders-usecase';
import { useListFinancial } from './financial-list.hook';
import { ReprocessOrderPaymentsUseCase } from '../../../Usecase/OrderPayments/reprocess-order-payments-usecase';
import { BillingFlowEnum, OrderStatusEnum } from '../../../Services/api/orders/orders.interface';
import { OrderStatusCell } from '../../../Components/OrderStatusCell/order-status-cell.component';
import { billingFlowLabels, billingFlowOptions, billingFlowSuggestionTagColors, billingFlowTagColors, getBillingFlowSuggestion, getOrderBillingFlow, getOrderCouponCode } from '../../../utils/billingFlow';
import { FormatMoney } from '../../../utils/formatMoney';
import { TABLE_STICKY_MAX_HEIGHT } from '../../../Styles/table-layout';
import { UpdateOrdersUseCase } from '../../../Usecase/Orders/update-order-usecase';

interface IFinancialList {
  listOrdersUseCase: ListOrdersUseCase;
  billProductOrderOrdersUseCase: BillProductOrderOrdersUseCase
  reprocessOrderPaymentsUseCase: ReprocessOrderPaymentsUseCase
  updateOrdersUseCase: UpdateOrdersUseCase
}

const REWORK_OBSERVATIONS_MAX = 500;

function getReworkObservationsHelperText(value: string): string {
  const counter = `${value.length}/${REWORK_OBSERVATIONS_MAX}`;
  if (!value.trim()) {
    return `Obrigatório para faturamento Retrabalho · ${counter}`;
  }
  return `Será salvo no pedido antes do faturamento · ${counter}`;
}

const FinancialList : FC<IFinancialList> = ({
  listOrdersUseCase,
  billProductOrderOrdersUseCase,
  reprocessOrderPaymentsUseCase,
  updateOrdersUseCase,
}) => {
    const {
      listOrders,
      list,
      loading,
      loadError,
      setShowFiltersModal,
      showFiltersModal,
      billProductsOrder,
      orderSelectedToBill,
      setOrderSelectedToBill,
      handleFilterSelect,
      currentFilters,
      handleReprocessPayment,
      orderToReprocess,
      setOrderToReprocess,
      hasReprocessPermission,
      handlePageChangeOrders,
      handleUpdateSelectedOrderBillingFlow,
      handleSearch,
      totalRegisters,
      financialSortModel,
      handleSortModelChange,
    } = useListFinancial({
      listOrdersUseCase,
      billProductOrderOrdersUseCase,
      reprocessOrderPaymentsUseCase,
      updateOrdersUseCase,
    })

    const selectedCouponCode = getOrderCouponCode(orderSelectedToBill);
    const selectedFlowSuggestion = getBillingFlowSuggestion(selectedCouponCode);
    const selectedBillingFlow = getOrderBillingFlow(orderSelectedToBill);
    const selectedHasFlowSuggestion = !!selectedFlowSuggestion && selectedBillingFlow !== selectedFlowSuggestion;
    const [reworkObservations, setReworkObservations] = useState('');
    const isSelectedRework = selectedBillingFlow === BillingFlowEnum.REWORK;
    const canBillSelectedOrder = !isSelectedRework || !!reworkObservations.trim();

    useEffect(() => {
      listOrders({ page: 1, searchBy: '' })
    }, [listOrders])

    useEffect(() => {
      setReworkObservations(orderSelectedToBill?.observations || '')
    }, [orderSelectedToBill?.id, orderSelectedToBill?.observations])

    const orderToReprocessLabel = useMemo(() => {
      if (!orderToReprocess) return ''
      const order = list?.find((item) => item.id === orderToReprocess)
      return order?.nsOrderId ? `#${order.nsOrderId}` : orderToReprocess
    }, [orderToReprocess, list])

    const selectedOrderLabel = orderSelectedToBill?.nsOrderId
      ? `#${orderSelectedToBill.nsOrderId}`
      : 'selecionado'

    const columns = useMemo(() => {
        const createColumns: any[] = [
          {
            field: 'nsOrderId',
            headerName: 'Pedido',
            minWidth: 120,
            width: 130,
            sortable: true,
            renderCell: (params: any) => (
              <Box
                component={Link}
                to={`/financial-details/${params.row.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                #{params.value}
              </Box>
            ),
          },
          {
            field: 'store',
            headerName: 'Loja',
            width: 90,
            sortable: true,
            align: 'center',
            headerAlign: 'center',
          },
          {
            field: 'createdAt',
            headerName: 'Data',
            width: 110,
            sortable: true,
          },
          {
            field: 'customer',
            headerName: 'Cliente',
            minWidth: 160,
            flex: 1,
            sortable: true,
            valueGetter: (_value: unknown, row: any) => row?.customer?.name,
            renderCell: (params: any) => (
              <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35 }}>
                {params?.row?.customer?.name || 'Não informado'}
              </Typography>
            ),
          },
          {
            field: 'totalValue',
            headerName: 'Total',
            width: 120,
            sortable: true,
            type: 'number',
            align: 'right',
            headerAlign: 'right',
            valueGetter: (_value: unknown, row: any) => Number(row?.totalValue) || 0,
            renderCell: (params: any) => (
              <Typography variant="body2" fontWeight={600}>
                {params?.row?.totalValue != null && params?.row?.totalValue !== ''
                  ? FormatMoney(Number(params.row.totalValue))
                  : '—'}
              </Typography>
            ),
          },
          {
            field: 'products',
            headerName: 'Itens',
            width: 80,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: (params: any) => (
              <Chip size="small" label={params?.row?.products?.length ?? 0} variant="outlined" />
            ),
          },
          {
            field: 'status',
            headerName: 'Status',
            headerAlign: 'left',
            align: 'left',
            minWidth: 260,
            flex: 1.2,
            sortable: false,
            renderCell: (params: any) => (
              <OrderStatusCell
                status={params.row.status}
                productionStatus={params.row.productionStatus}
                errorMessage={params.row.errorMessage}
              />
            ),
          },
          {
            field: 'billing_flow',
            headerName: 'Fluxo',
            headerAlign: 'center',
            align: 'center',
            width: 140,
            sortable: false,
            renderCell: (params: any) => {
              const flow = getOrderBillingFlow(params.row);
              const couponCode = getOrderCouponCode(params.row);
              const flowSuggestion = getBillingFlowSuggestion(couponCode);
              const hasFlowSuggestion = !!flowSuggestion && flow !== flowSuggestion;
              const flowTagColor = hasFlowSuggestion
                ? billingFlowSuggestionTagColors
                : billingFlowTagColors[flow];

              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, py: 0.5 }}>
                  <Tag
                    title={billingFlowLabels[flow]}
                    boxStyle={{
                      backgroundColor: flowTagColor.backgroundColor,
                      color: flowTagColor.textColor,
                      padding: '6px 10px',
                    }}
                    titleStyle={{ color: flowTagColor.textColor, fontSize: '0.75rem' }}
                  />
                  {hasFlowSuggestion && flowSuggestion && (
                    <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, color: billingFlowSuggestionTagColors.textColor, textAlign: 'center', lineHeight: 1.2 }}>
                      Cupom: {billingFlowLabels[flowSuggestion]}
                    </Typography>
                  )}
                </Box>
              )
            },
          },
          {
            field: "actions",
            headerName: "Ações",
            width: 80,
            sortable: false,
            renderCell: (params: any) => (
              <ButtonActionMenu
                options={[
                  {
                    title: 'Ver detalhes',
                    href: `/financial-details/${params.row.id}`,
                    openInNewTab: true,
                    icon: <FaEye />,
                  },
                  ...(params.row.status === OrderStatusEnum.AWAITING_INVOICING ? [{
                    title: 'Faturar todos os produtos',
                    onClick: () => setOrderSelectedToBill(params.row),
                    icon: <MdAttachMoney />,
                  }] : []),
                  ...(hasReprocessPermission ? [{
                    title: 'Reprocessar pagamento',
                    onClick: () => setOrderToReprocess(params.row.id),
                    icon: <FaRegCreditCard />,
                  }] : []),
                ]}
              />
            ),
          },
        ];

        return createColumns;
      }, [hasReprocessPermission, setOrderSelectedToBill, setOrderToReprocess]);

    return (
      <Can permissions={['menu:financial:manage']}>
      <ConfirmDialog
        title="Reprocessar pagamento"
        onCancel={() => setOrderToReprocess('')}
        description={`Tem certeza que deseja reprocessar o pagamento do pedido ${orderToReprocessLabel || 'selecionado'}?`}
        open={!!orderToReprocess}
        onConfirm={() => handleReprocessPayment(orderToReprocess || '')}
      />
        <BaseScreen>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
              <OrderFiltersDrawer
                handleSelect={handleFilterSelect}
                open={showFiltersModal}
                title="Filtros"
                onToggle={() => setShowFiltersModal(prev => !prev)}
              />

              <ConfirmDialog
                open={!!orderSelectedToBill}
                title="Faturar todo o pedido"
                description={selectedHasFlowSuggestion ? 'Atenção: encontramos uma sugestão de fluxo diferente antes de faturar.' : isSelectedRework ? `Faturamento de retrabalho do pedido ${selectedOrderLabel} exige observações.` : `Tem certeza que deseja faturar o pedido ${selectedOrderLabel}?`}
                onCancel={() => setOrderSelectedToBill(undefined)}
                onConfirm={() => billProductsOrder(reworkObservations)}
                confirmButtonTitle="Faturar pedido"
                confirmDisabled={!canBillSelectedOrder}
              >
                {selectedHasFlowSuggestion && selectedFlowSuggestion && (
                  <Alert severity="warning" sx={{ marginTop: '16px' }}>
                    <Stack spacing={1}>
                      <Typography sx={{ fontSize: '18px', fontWeight: 700 }}>
                        Cupom {selectedCouponCode} indica {billingFlowLabels[selectedFlowSuggestion]}
                      </Typography>
                      <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
                        Fluxo atual: {billingFlowLabels[selectedBillingFlow]}. Altere o fluxo antes de faturar para evitar emissão no tipo fiscal errado.
                      </Typography>
                      <FormControl fullWidth sx={{ marginTop: '8px', backgroundColor: '#fff' }}>
                        <InputLabel id="selected-order-billing-flow-label">Fluxo de faturamento</InputLabel>
                        <Select
                          labelId="selected-order-billing-flow-label"
                          label="Fluxo de faturamento"
                          value={selectedBillingFlow}
                          onChange={(event) => handleUpdateSelectedOrderBillingFlow(event.target.value as BillingFlowEnum)}
                        >
                          {billingFlowOptions.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {isSelectedRework && (
                        <TextField
                          required
                          multiline
                          minRows={3}
                          label="Motivo do retrabalho"
                          placeholder="Descreva o motivo do retrabalho antes de faturar"
                          value={reworkObservations}
                          onChange={(event) => setReworkObservations(event.target.value)}
                          error={!reworkObservations.trim()}
                          helperText={getReworkObservationsHelperText(reworkObservations)}
                          inputProps={{ maxLength: REWORK_OBSERVATIONS_MAX }}
                          sx={{ backgroundColor: '#fff' }}
                        />
                      )}
                    </Stack>
                  </Alert>
                )}
                {!selectedHasFlowSuggestion && isSelectedRework && (
                  <TextField
                    required
                    fullWidth
                    multiline
                    minRows={3}
                    label="Motivo do retrabalho"
                    placeholder="Descreva o motivo do retrabalho antes de faturar"
                    value={reworkObservations}
                    onChange={(event) => setReworkObservations(event.target.value)}
                    error={!reworkObservations.trim()}
                    helperText={getReworkObservationsHelperText(reworkObservations)}
                    inputProps={{ maxLength: REWORK_OBSERVATIONS_MAX }}
                    sx={{ marginTop: '16px' }}
                  />
                )}
              </ConfirmDialog>

                <BaseContentPage
                    title="Financeiro"
                    subtitle="Gerencie faturamento, pagamentos e fluxo fiscal dos pedidos"
                    minHeight="auto"
                    breadcrumb={[
                      { title: 'Financeiro', link: "/financial-list" },
                      { title: 'Listagem' },
                    ]}
                    search
                    searchDefaultValue={currentFilters.searchBy ?? ''}
                    searchFunction={handleSearch}
                    searchLoading={loading && !!list?.length}
                    placeholderSearch="Pesquisar por pedido ou cliente"
                    toolbarActions={() => (
                      <Button
                        LeftIcon={() => <IoFilterOutline size={18} />}
                        sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { sm: 100 }, flexShrink: 0 }}
                        boxLeftSx={{ gap: '10px', marginLeft: '5px' }}
                        textSx={{ textTransform: 'Capitalize', fontWeight: 500, whiteSpace: 'nowrap' }}
                        onClick={() => setShowFiltersModal(true)}
                      >
                        Filtros
                      </Button>
                    )}
                >
                  {!!list?.length ? (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Table
                        data={list}
                        columns={columns}
                        totalCount={totalRegisters}
                        search={currentFilters.searchBy}
                        page={currentFilters.page}
                        onPageChange={handlePageChangeOrders}
                        sortModel={financialSortModel}
                        sortingOrder={['asc', 'desc']}
                        sortingMode="server"
                        onSortModelChange={handleSortModelChange}
                        rowHeight={76}
                        stickyHeader
                        maxHeight={TABLE_STICKY_MAX_HEIGHT}
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  ) : loading ? (
                    <TableListSkeleton rowHeight={76} />
                  ) : loadError ? (
                    <Placeholder
                      description="Não foi possível carregar os pedidos financeiros."
                      onRetry={() => listOrders({ page: currentFilters.page })}
                    />
                  ) : (
                    <Placeholder description="Nenhum pedido financeiro encontrado." />
                  )}
                </BaseContentPage>
            </Paper>
        </BaseScreen>
        </Can>
    )
}

export { FinancialList };
